import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, Calendar, Mail, Mic, BookOpen, Users, Award, 
  Heart, Star, Check, Send, Loader2, Play, ExternalLink,
  Linkedin, Instagram, Facebook, ShoppingBag
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Images
const IMAGES = {
  together: 'https://customer-assets.emergentagent.com/job_1d2f3656-52d0-4fe2-8a37-9f88482f6558/artifacts/jnhn37h5_crystal%20and%20Dr.%20Nicole%20image.png',
  crystal: 'https://i.ontraport.com/260712.ab2146fd52a3bec566c017ab26b740c5.PNG',
  nicole: 'https://i.ontraport.com/260712.96ce8faf24d6dd5d62bfdaa000961710.PNG',
  yummyPodcast: 'https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/k2mccd5b_yummy%20podcast.jpg'
};

const YUMMY_PODCAST_URL = 'https://www.youtube.com/watch?v=WT3CbPtA2O8&list=PLOoPQBhzQWf4Qnqn9WeHfKUaUoFrXBD8W&index=3';

// Podcast platform links
const PODCAST_PLATFORMS = {
  youtube: 'https://www.youtube.com/playlist?list=PLOoPQBhzQWf4Qnqn9WeHfKUaUoFrXBD8W',
  apple: 'https://podcasts.apple.com/us/podcast/yummy-podcast/id1709678796',
  spotify: 'https://open.spotify.com/show/75yah0Khbyuz0zIWR249bt',
  amazon: 'https://music.amazon.com/podcasts/e7ce3dfd-2212-4746-a46d-1cdf5e768ed9/yummy-podcast',
  iheart: 'https://www.iheart.com/podcast/269-yummy-podcast-125905582/'
};

const SPEAKERS_KIT_URL = 'https://www.canva.com/design/DAGeLWqX6-4/xRmc0KeiPLk2lOZrn72jtQ/view?utm_content=DAGeLWqX6-4&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=h34117753db';

// Speaking client logos (companies where Crystal & Dr. Nicole have spoken)
const SPEAKING_CLIENT_LOGOS = {
  nasa: 'https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/o9xr3k3v_NASA%20logo.png',
  nahse: 'https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/j70zhdu3_NAHSE%20logo.jpeg',
  anywhere: 'https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/km2yfg4x_anywhere%20logo.png',
  porsche: 'https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/67x3chob_porsche%20logotype%202.png',
  coldwellBanker: 'https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/2zga2olx_coldwell%20banker%20logo.png',
  cocaCola: 'https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/hvqnw8pp_cocacola%20logo.png'
};

// Books
const BOOKS = {
  crystal: {
    title: "Hard Workers Work Hard, and Networkers Move Up!",
    subtitle: "Accelerate Your Career 10X Faster",
    author: "Crystal Khalil",
    image: 'https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/s45kyxav_ms.%20c%20book%5B63%5D.jpg',
    amazonUrl: 'https://www.amazon.com/HARD-WORKERS-WORK-NETWORKERS-MOVE-ebook/dp/B07WG42BQ6/'
  },
  nicole: {
    title: "A Woman's True Purpose",
    subtitle: "Live Like You Matter",
    author: "Dr. Nicole LaBeach",
    image: 'https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/jm7sph8y_dr.%20n%20%20book.jpg',
    amazonUrl: 'https://www.amazon.com/Womans-Purpose-Nicole-LaBeach-2007-04-01/dp/B01K3NGZXY/'
  }
};

// Panel/Speaking Image
const PANEL_IMAGE = 'https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/zxqys4tk_crystal%20and%20dr%20nicole%20seated.jpg';

// Featured media logos from Crystal and Dr. Nicole website (Ontraport CDN)
const MEDIA_LOGOS = {
  own: 'https://i.ontraport.com/260712.0f888b3fbe99cf20f4c34f7dc9643939.PNG',
  cbsRadio: 'https://i.ontraport.com/260712.71a22efd1eedd5f2fda890575d3e1bd2.PNG',
  iHeartRadio: 'https://i.ontraport.com/260712.82e91817d856e84436344e034541650c.PNG',
  steveHarvey: 'https://i.ontraport.com/260712.7969fd85bb19c10e8db9c3085e47ab0b.PNG',
  spotify: 'https://i.ontraport.com/260712.8027fa1c3e8e2a4a46b2097c8854007a.PNG',
  goodDayAtlanta: 'https://i.ontraport.com/260712.db99291a445221892b554d7e535afad6.PNG',
  youtube: 'https://i.ontraport.com/260712.1531986f9b8c67857e59a3e22a996ede.PNG',
  gma: 'https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/6mtokgxu_gma%20logo.png',
  tamronHall: 'https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/20gjxgqu_tamronhall%20logo.png'
};

// Custom logo component for Speaking clients (SVG based)
const MediaLogo = ({ name, className = "" }) => {
  const logos = {
    TEDx: (
      <svg viewBox="0 0 100 40" className={className}>
        <rect x="2" y="8" width="96" height="24" fill="#E62B1E" rx="2"/>
        <text x="50%" y="55%" dominantBaseline="middle" textAnchor="middle" fill="white" style={{ fontSize: '18px', fontWeight: 'bold', fontFamily: 'sans-serif', letterSpacing: '2px' }}>TEDx</text>
      </svg>
    ),
    NASA: (
      <svg viewBox="0 0 100 50" className={className}>
        <circle cx="50" cy="25" r="22" fill="#0B3D91"/>
        <text x="50%" y="55%" dominantBaseline="middle" textAnchor="middle" fill="white" style={{ fontSize: '14px', fontWeight: 'bold', fontFamily: 'sans-serif' }}>NASA</text>
      </svg>
    ),
    Porsche: (
      <svg viewBox="0 0 120 40" className={className}>
        <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" className="fill-current" style={{ fontSize: '16px', fontWeight: 'bold', fontFamily: 'serif', letterSpacing: '3px' }}>PORSCHE</text>
      </svg>
    ),
    CocaCola: (
      <svg viewBox="0 0 120 40" className={className}>
        <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" fill="#E31837" style={{ fontSize: '16px', fontWeight: 'bold', fontFamily: 'cursive' }}>Coca-Cola</text>
      </svg>
    ),
    NAHSE: (
      <svg viewBox="0 0 100 40" className={className}>
        <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" className="fill-current" style={{ fontSize: '18px', fontWeight: 'bold', fontFamily: 'sans-serif' }}>NAHSE</text>
      </svg>
    ),
    ColdwellBanker: (
      <svg viewBox="0 0 140 40" className={className}>
        <rect x="2" y="12" width="136" height="16" fill="#012169" rx="2"/>
        <text x="50%" y="55%" dominantBaseline="middle" textAnchor="middle" fill="white" style={{ fontSize: '10px', fontWeight: 'bold', fontFamily: 'sans-serif', letterSpacing: '1px' }}>COLDWELL BANKER</text>
      </svg>
    ),
    AnywhereRealty: (
      <svg viewBox="0 0 140 40" className={className}>
        <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" className="fill-current" style={{ fontSize: '12px', fontWeight: 'bold', fontFamily: 'sans-serif' }}>ANYWHERE REALTY</text>
      </svg>
    )
  };
  return logos[name] || null;
};

const FounderProfilePage = () => {
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    phone: '',
    event_type: '',
    event_date: '',
    audience_size: '',
    topic_interest: '',
    message: ''
  });

  const handleSubmitInquiry = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.event_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/speaking-inquiry`, formData);
      toast.success('Inquiry submitted! We will be in touch soon.');
      setShowBookingForm(false);
      setFormData({
        name: '',
        email: '',
        organization: '',
        phone: '',
        event_type: '',
        event_date: '',
        audience_size: '',
        topic_interest: '',
        message: ''
      });
    } catch (error) {
      console.error('Failed to submit inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const speakingTopics = [
    "Leadership & Executive Presence",
    "Women in Leadership",
    "Relationship Mastery",
    "Team Building & Culture",
    "Personal Transformation",
    "Work-Life Integration",
    "Faith & Purpose in Business"
  ];

  const speakingClients = [
    { name: "TEDx", highlight: true, logoKey: "TEDx", logoImg: null },
    { name: "NASA", highlight: true, logoKey: "NASA", logoImg: SPEAKING_CLIENT_LOGOS.nasa },
    { name: "Porsche", highlight: true, logoKey: "Porsche", logoImg: SPEAKING_CLIENT_LOGOS.porsche },
    { name: "Coca-Cola", highlight: false, logoKey: "CocaCola", logoImg: SPEAKING_CLIENT_LOGOS.cocaCola },
    { name: "NAHSE", highlight: false, logoKey: "NAHSE", logoImg: SPEAKING_CLIENT_LOGOS.nahse },
    { name: "Coldwell Banker", highlight: false, logoKey: "ColdwellBanker", logoImg: SPEAKING_CLIENT_LOGOS.coldwellBanker },
    { name: "Anywhere Realty", highlight: false, logoKey: "AnywhereRealty", logoImg: SPEAKING_CLIENT_LOGOS.anywhere }
  ];

  const featuredOn = [
    { name: "Good Morning America", logo: MEDIA_LOGOS.gma, darkBg: true },
    { name: "OWN Network", logo: MEDIA_LOGOS.own, darkBg: true },
    { name: "CBS Radio", logo: MEDIA_LOGOS.cbsRadio, darkBg: true },
    { name: "iHeartRadio", logo: MEDIA_LOGOS.iHeartRadio, darkBg: false },
    { name: "Steve Harvey Morning Show", logo: MEDIA_LOGOS.steveHarvey, darkBg: true },
    { name: "Good Day Atlanta", logo: MEDIA_LOGOS.goodDayAtlanta, darkBg: true },
    { name: "Tamron Hall Show", logo: MEDIA_LOGOS.tamronHall, darkBg: true },
    { name: "Spotify", logo: MEDIA_LOGOS.spotify, darkBg: false }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-20" style={{ background: 'linear-gradient(135deg, #2C4A3E 0%, #1a332a 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Link to="/" className="inline-flex items-center text-white/70 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={IMAGES.together} 
                  alt="Crystal Khalil & Dr. Nicole LaBeach" 
                  className="w-full h-auto"
                />
              </div>
              {/* Social Links */}
              <div className="flex items-center gap-4 mt-6 justify-center lg:justify-start">
                <a href="https://www.instagram.com/crystalanddrnicole/" target="_blank" rel="noopener noreferrer" 
                   className="h-10 w-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors">
                  <Instagram className="h-5 w-5 text-white" />
                </a>
                <a href="https://www.linkedin.com/company/sister-diamonds-llc/" target="_blank" rel="noopener noreferrer"
                   className="h-10 w-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors">
                  <Linkedin className="h-5 w-5 text-white" />
                </a>
                <a href="https://www.facebook.com/sisterdiamondsllc" target="_blank" rel="noopener noreferrer"
                   className="h-10 w-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors">
                  <Facebook className="h-5 w-5 text-white" />
                </a>
              </div>
            </div>

            {/* Right: Content */}
            <div className="text-white">
              <div className="inline-flex items-center gap-2 bg-terracotta/20 px-4 py-2 rounded-full mb-4">
                <Award className="h-4 w-4 text-terracotta" />
                <span className="text-sm font-medium text-terracotta">Co-Founders & Wonder Twin Coaches</span>
              </div>
              
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl mb-4">
                Crystal Khalil & Dr. Nicole LaBeach
              </h1>
              
              <p className="text-xl text-white/80 mb-6 leading-relaxed">
                Together, igniting a global movement to help leaders, dreamers, and doers reset, rise, 
                and reclaim everything they were made for.
              </p>

              <p className="text-white/70 mb-8">
                With over <span className="text-terracotta font-semibold">50 years of combined expertise</span>, 
                they create transformational spaces where growth-minded individuals break barriers, 
                build legacies, and unleash their fullest potential—in business, relationships, and life.
              </p>

              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => setShowBookingForm(true)}
                  className="bg-terracotta hover:bg-terracotta/90 text-white h-12 px-6"
                  data-testid="book-speaking-btn"
                >
                  <Mic className="h-4 w-4 mr-2" />
                  Book for Speaking
                </Button>
                <a href="https://calendly.com/crystalanddrnicole/contactus" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 h-12 px-6">
                    <Calendar className="h-4 w-4 mr-2" />
                    Schedule a Call
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* As Featured On */}
      <section className="py-16 bg-gradient-to-r from-sand/30 via-white to-sand/30 border-b border-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-sm font-medium uppercase tracking-widest text-terracotta mb-2">National Media Recognition</p>
            <p className="text-2xl font-heading text-forest">As Seen On</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 items-center">
            {featuredOn.map((outlet, idx) => (
              <div key={idx} className="text-center group">
                <div className={`rounded-xl py-4 px-3 shadow-sm hover:shadow-lg transition-all duration-300 border border-sand/50 group-hover:border-terracotta/30 group-hover:-translate-y-1 h-20 flex items-center justify-center ${
                  outlet.darkBg ? 'bg-forest' : 'bg-white'
                }`}>
                  <img 
                    src={outlet.logo} 
                    alt={outlet.name}
                    className="max-h-12 max-w-full object-contain"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Individual Bios */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl text-forest mb-4">Meet the Wonder Twins</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Not just coaches—curators of sacred experiences. Rooms shift when they enter. Legacies are revitalized when they lead.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Crystal */}
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className="h-[420px] overflow-hidden bg-gradient-to-br from-pink-200 to-purple-300">
                <img src={IMAGES.crystal} alt="Crystal Khalil" className="w-full h-full object-cover object-top" />
              </div>
              <CardContent className="p-8">
                <h3 className="font-heading text-2xl text-forest mb-2">Crystal Khalil</h3>
                <p className="text-terracotta font-medium mb-4">The Divine Disruptor & Destiny Dealer</p>
                <p className="text-gray-600 mb-4">
                  She&apos;s the strategist with soul, the executive whisperer, and the woman you call when 
                  you&apos;re ready to trade burnout for brilliance. From boardrooms to Bible study, Guyana to Atlanta, 
                  she moves with purpose and power.
                </p>
                <p className="text-gray-600 mb-4">
                  With <span className="font-semibold text-forest">30+ years of corporate savvy</span> and a heart 
                  that beats for service, she&apos;s built a movement through Sister Diamonds and Volition Enterprises, 
                  empowering high-achieving leaders to unbind, unleash, and live their ultimate, most meaningful life yet.
                </p>
                <p className="text-forest font-medium italic">
                  &ldquo;A Destiny Helper in heels. Get ready, because better is on the horizon.&rdquo;
                </p>
              </CardContent>
            </Card>

            {/* Dr. Nicole */}
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className="h-[420px] overflow-hidden bg-gradient-to-br from-blue-200 to-indigo-300">
                <img src={IMAGES.nicole} alt="Dr. Nicole LaBeach" className="w-full h-full object-cover object-top" />
              </div>
              <CardContent className="p-8">
                <h3 className="font-heading text-2xl text-forest mb-2">Dr. Nicole LaBeach</h3>
                <p className="text-terracotta font-medium mb-4">The Soul Surgeon & Sacred Strategist</p>
                <p className="text-gray-600 mb-4">
                  The Master Coach who speaks fluent breakthrough. She&apos;s the woman CEOs meet with gratitude, 
                  the voice of wisdom you didn&apos;t know you needed, and the one who helps powerhouse men and women 
                  take the mask off, for good.
                </p>
                <p className="text-gray-600 mb-4">
                  With a <span className="font-semibold text-forest">Ph.D. in psychology and 25+ years of experience</span>, 
                  Dr. Nicole served as the 3-season host of <em>Put A Ring On It</em> on OWN and has been featured on 
                  GMA, CBS Radio, Oprah Daily, Essence Magazine, and The Tamron Hall Show.
                </p>
                <p className="text-forest font-medium italic">
                  &ldquo;She doesn&apos;t just teach vision—she resurrects it.&rdquo;
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What Sets Them Apart */}
      <section className="py-20 bg-forest text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl mb-4">What Sets Us Apart</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Users,
                title: "Dual-Lens Expertise",
                description: "Uniquely blending executive leadership coaching with relationship mastery—winning at work AND at home."
              },
              {
                icon: Heart,
                title: "Built-in Ecosystem",
                description: "Live masterminds, thriving community, and hands-on support services that fuel real growth."
              },
              {
                icon: Star,
                title: "Destiny Helper Approach",
                description: "Walking alongside you with clarity, strategy, community, and the push you need to break barriers."
              },
              {
                icon: BookOpen,
                title: "Faith-Rooted, Strategy-Driven",
                description: "Aligning your purpose with proven strategies to move boldly toward your highest calling."
              }
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="h-16 w-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="h-8 w-8 text-terracotta" />
                </div>
                <h3 className="font-heading text-xl mb-2">{item.title}</h3>
                <p className="text-white/70 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Speaking Topics */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-terracotta font-medium text-sm uppercase tracking-wide">Speaking & Keynotes</span>
              <h2 className="font-heading text-3xl md:text-4xl text-forest mt-2 mb-6">
                Ignite Transformation in Your Organization
              </h2>
              <p className="text-gray-600 mb-6">
                Crystal and Dr. Nicole deliver dynamic keynotes, workshops, and corporate training programs 
                that inspire teams to lead with authenticity, build stronger relationships, and create lasting impact.
              </p>

              {/* Prestigious Clients Badge */}
              <div className="bg-gradient-to-r from-forest/5 via-terracotta/5 to-forest/5 rounded-xl p-6 mb-8 border border-forest/10">
                <p className="text-sm text-gray-500 uppercase tracking-wider mb-4 font-medium">Trusted by World-Class Organizations</p>
                <div className="flex flex-wrap gap-4 items-center">
                  {speakingClients.map((client, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-center justify-center px-4 py-3 rounded-xl transition-all min-w-[120px] h-16 ${
                        client.highlight 
                          ? 'bg-white shadow-md border border-terracotta/20' 
                          : 'bg-white/50 border border-forest/10'
                      }`}
                    >
                      {client.logoImg ? (
                        <img 
                          src={client.logoImg} 
                          alt={client.name}
                          className="max-h-10 max-w-[100px] object-contain"
                        />
                      ) : (
                        <MediaLogo 
                          name={client.logoKey} 
                          className={`h-8 w-full ${client.highlight ? 'text-forest' : 'text-forest/70'}`} 
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3 mb-8">
                {speakingTopics.map((topic, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="h-6 w-6 bg-terracotta/10 rounded-full flex items-center justify-center">
                      <Check className="h-4 w-4 text-terracotta" />
                    </div>
                    <span className="text-gray-700">{topic}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => setShowBookingForm(true)}
                  className="bg-forest hover:bg-forest/90 text-white h-12 px-8"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Inquire About Speaking
                </Button>
                <a 
                  href={SPEAKERS_KIT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center h-12 px-8 border-2 border-terracotta text-terracotta hover:bg-terracotta hover:text-white rounded-md font-medium transition-colors"
                  data-testid="speakers-kit-btn"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Speaker's Kit
                </a>
              </div>
            </div>

            <div className="bg-sand/30 rounded-2xl p-8">
              <div className="aspect-[4/3] bg-purple-900 rounded-xl flex items-center justify-center mb-6 relative overflow-hidden">
                <img 
                  src={IMAGES.yummyPodcast}
                  alt="YUMMY Podcast"
                  className="absolute inset-0 w-full h-full object-contain"
                />
                <a href={YUMMY_PODCAST_URL}
                   target="_blank" rel="noopener noreferrer"
                   className="h-16 w-16 bg-terracotta rounded-full flex items-center justify-center hover:scale-110 transition-transform z-10 shadow-xl">
                  <Play className="h-8 w-8 text-white ml-1" />
                </a>
              </div>
              <h3 className="font-heading text-xl text-forest mb-2">YUMMY Podcast</h3>
              <p className="text-gray-600 text-sm mb-4">
                Your Ultimate Most Meaningful Yet—candid conversations, expert insights, 
                and personal anecdotes exploring ways to break free and cultivate authenticity.
              </p>
              
              {/* Podcast Platform Links */}
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Listen On</p>
              <div className="flex flex-wrap gap-2">
                <a href={PODCAST_PLATFORMS.apple} target="_blank" rel="noopener noreferrer"
                   className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-full hover:bg-gray-800 transition-colors">
                  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
                  </svg>
                  Apple
                </a>
                <a href={PODCAST_PLATFORMS.spotify} target="_blank" rel="noopener noreferrer"
                   className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1DB954] text-white text-xs rounded-full hover:bg-[#1aa34a] transition-colors">
                  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                  </svg>
                  Spotify
                </a>
                <a href={PODCAST_PLATFORMS.amazon} target="_blank" rel="noopener noreferrer"
                   className="flex items-center gap-1.5 px-3 py-1.5 bg-[#00A8E1] text-white text-xs rounded-full hover:bg-[#0095c8] transition-colors">
                  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z"/>
                  </svg>
                  Amazon
                </a>
                <a href={PODCAST_PLATFORMS.iheart} target="_blank" rel="noopener noreferrer"
                   className="flex items-center gap-1.5 px-3 py-1.5 bg-[#C6002B] text-white text-xs rounded-full hover:bg-[#a80024] transition-colors">
                  <Heart className="h-3.5 w-3.5 fill-current" />
                  iHeart
                </a>
                <a href={PODCAST_PLATFORMS.youtube} target="_blank" rel="noopener noreferrer"
                   className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FF0000] text-white text-xs rounded-full hover:bg-[#cc0000] transition-colors">
                  <Play className="h-3.5 w-3.5 fill-current" />
                  YouTube
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Panel/Speaking Image */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <img 
              src={PANEL_IMAGE}
              alt="Crystal and Dr. Nicole speaking on stage"
              className="w-full h-auto object-cover"
            />
          </div>
          <p className="text-center text-gray-500 text-sm mt-4">Crystal & Dr. Nicole speaking at SOULMATES panel</p>
        </div>
      </section>

      {/* Books Section */}
      <section className="py-20 bg-gradient-to-b from-sand/30 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-terracotta font-medium text-sm uppercase tracking-wide">Published Authors</span>
            <h2 className="font-heading text-3xl md:text-4xl text-forest mt-2">
              Our Books
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Transform your mindset and accelerate your growth with these powerful guides from Crystal and Dr. Nicole.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {/* Crystal's Book */}
            <div className="flex flex-col items-center text-center group">
              <a 
                href={BOOKS.crystal.amazonUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block mb-6 transform group-hover:scale-105 transition-transform duration-300"
              >
                <div className="relative">
                  <img 
                    src={BOOKS.crystal.image}
                    alt={BOOKS.crystal.title}
                    className="w-64 h-auto shadow-2xl rounded-lg"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 rounded-lg transition-colors" />
                </div>
              </a>
              <h3 className="font-heading text-xl text-forest mb-1">{BOOKS.crystal.title}</h3>
              <p className="text-gray-500 text-sm mb-2">{BOOKS.crystal.subtitle}</p>
              <p className="text-terracotta font-medium mb-4">by {BOOKS.crystal.author}</p>
              <a 
                href={BOOKS.crystal.amazonUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-forest text-white px-6 py-3 rounded-lg hover:bg-forest/90 transition-colors font-medium"
              >
                <ShoppingBag className="h-4 w-4" />
                Buy on Amazon
              </a>
            </div>

            {/* Dr. Nicole's Book */}
            <div className="flex flex-col items-center text-center group">
              <a 
                href={BOOKS.nicole.amazonUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block mb-6 transform group-hover:scale-105 transition-transform duration-300"
              >
                <div className="relative">
                  <img 
                    src={BOOKS.nicole.image}
                    alt={BOOKS.nicole.title}
                    className="w-64 h-auto shadow-2xl rounded-lg"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 rounded-lg transition-colors" />
                </div>
              </a>
              <h3 className="font-heading text-xl text-forest mb-1">{BOOKS.nicole.title}</h3>
              <p className="text-gray-500 text-sm mb-2">{BOOKS.nicole.subtitle}</p>
              <p className="text-terracotta font-medium mb-4">by {BOOKS.nicole.author}</p>
              <a 
                href={BOOKS.nicole.amazonUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-forest text-white px-6 py-3 rounded-lg hover:bg-forest/90 transition-colors font-medium"
              >
                <ShoppingBag className="h-4 w-4" />
                Buy on Amazon
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 bg-sand/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-6 w-6 text-terracotta fill-terracotta" />
            ))}
          </div>
          <blockquote className="font-heading text-2xl md:text-3xl text-forest mb-6 leading-relaxed">
            &ldquo;I am really grateful for the opportunity to work with Dr. Nicole and Crystal. 
            They were just personal, inspiring and life-changing. If you&apos;re interested in making 
            a change in your life and going to the next level, become a part of the Sister Diamond Collective.&rdquo;
          </blockquote>
          <p className="text-terracotta font-medium">Dr. Ursula Whitehead</p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-forest text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl mb-4">Ready to Unleash Your Potential?</h2>
          <p className="text-white/80 text-lg mb-8">
            Whether you&apos;re seeking executive coaching, relationship guidance, or a transformative speaker 
            for your next event—Crystal and Dr. Nicole are here to help you step into your power.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={() => setShowBookingForm(true)}
              className="bg-terracotta hover:bg-terracotta/90 text-white h-12 px-8"
            >
              <Mic className="h-4 w-4 mr-2" />
              Book for Speaking
            </Button>
            <a href="https://calendly.com/crystalanddrnicole/contactus" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 h-12 px-8">
                <Calendar className="h-4 w-4 mr-2" />
                Schedule a Discovery Call
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Speaking Inquiry Form Dialog */}
      <Dialog open={showBookingForm} onOpenChange={setShowBookingForm}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-forest">Speaking & Booking Inquiry</DialogTitle>
            <DialogDescription>
              Interested in having Crystal & Dr. Nicole speak at your event? Fill out the form below and we&apos;ll be in touch.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmitInquiry} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Your Name *</Label>
                <Input 
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Full name"
                  required
                  data-testid="inquiry-name"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input 
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="you@company.com"
                  required
                  data-testid="inquiry-email"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Organization</Label>
                <Input 
                  value={formData.organization}
                  onChange={(e) => setFormData(prev => ({ ...prev, organization: e.target.value }))}
                  placeholder="Company or organization"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input 
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="(555) 555-5555"
                />
              </div>
            </div>

            <div>
              <Label>Event Type *</Label>
              <Select 
                value={formData.event_type}
                onValueChange={(val) => setFormData(prev => ({ ...prev, event_type: val }))}
              >
                <SelectTrigger data-testid="inquiry-event-type">
                  <SelectValue placeholder="Select event type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="keynote">Keynote Speech</SelectItem>
                  <SelectItem value="workshop">Workshop / Training</SelectItem>
                  <SelectItem value="corporate">Corporate Event</SelectItem>
                  <SelectItem value="conference">Conference</SelectItem>
                  <SelectItem value="retreat">Retreat</SelectItem>
                  <SelectItem value="podcast">Podcast / Media</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Proposed Date</Label>
                <Input 
                  type="date"
                  value={formData.event_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, event_date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Expected Audience Size</Label>
                <Select 
                  value={formData.audience_size}
                  onValueChange={(val) => setFormData(prev => ({ ...prev, audience_size: val }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under-50">Under 50</SelectItem>
                    <SelectItem value="50-100">50-100</SelectItem>
                    <SelectItem value="100-500">100-500</SelectItem>
                    <SelectItem value="500-1000">500-1,000</SelectItem>
                    <SelectItem value="over-1000">Over 1,000</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Topic of Interest</Label>
              <Select 
                value={formData.topic_interest}
                onValueChange={(val) => setFormData(prev => ({ ...prev, topic_interest: val }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select topic" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="leadership">Leadership & Executive Presence</SelectItem>
                  <SelectItem value="women-leadership">Women in Leadership</SelectItem>
                  <SelectItem value="relationships">Relationship Mastery</SelectItem>
                  <SelectItem value="team-building">Team Building & Culture</SelectItem>
                  <SelectItem value="transformation">Personal Transformation</SelectItem>
                  <SelectItem value="work-life">Work-Life Integration</SelectItem>
                  <SelectItem value="faith-purpose">Faith & Purpose in Business</SelectItem>
                  <SelectItem value="custom">Custom Topic</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Additional Details</Label>
              <Textarea 
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Tell us more about your event, goals, and any specific requirements..."
                rows={4}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowBookingForm(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                className="flex-1 bg-forest hover:bg-forest/90 text-white"
                data-testid="inquiry-submit"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</>
                ) : (
                  <><Send className="h-4 w-4 mr-2" />Send Inquiry</>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default FounderProfilePage;
